import { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { 
  Upload,
  FileText,
  Calendar,
  Type,
  Hash,
  ChevronDown,
  AlignLeft,
  ToggleLeft,
  Circle,
  X,
  CheckCircle,
  AlertCircle,
  Clock
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { getCurrentTenantSlug } from '@/utils/tenant-utils';
import { WorkspaceService } from '@/services/workspace-service';
import { monitoring } from '@/lib/monitoring';
import { errorTracking } from '@/lib/error-tracking';
import { performance } from '@/lib/performance';

type FieldType = 'text' | 'number' | 'date' | 'dropdown' | 'textarea' | 'boolean' | 'radio';

interface LogField {
  id: string;
  title: string;
  type: FieldType;
  required: boolean;
  options?: string[];
  useInName?: boolean; // New property to control if field should be used in submission name
}

interface ManualConfig {
  id: string;
  form_title: string;
  manufacturing_process_id: string;
  data_type?: string;
  config_data: {
    fields?: LogField[];
    formTitle?: string;
    dataConfig?: {
      partNumber: string;
      description: string;
      displayName: string;
      visualRequirement: string;
    };
    parsing_configuration_id?: string;
    part_configuration_id?: string;
  };
}

interface ManufacturingProcess {
  id: string;
  name: string;
}

interface UploadResult {
  fileName: string;
  success: boolean;
  error?: string;
  recordCount?: number;
}

interface UploadSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  results: UploadResult[];
}

interface ManualDataUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  station: ManufacturingProcess | null;
  manualConfigs: ManualConfig[];
  productId?: string;
  onSubmissionSaved?: () => void;
}

const UploadSummaryModal = ({ isOpen, onClose, results }: UploadSummaryModalProps) => {
  const successCount = results.filter(r => r.success).length;
  const failureCount = results.filter(r => !r.success).length;
  const totalRecords = results.filter(r => r.success).reduce((sum, r) => sum + (r.recordCount || 0), 0);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Upload Summary
          </DialogTitle>
          <DialogDescription>
            Summary of file upload and processing results
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-green-600">{successCount}</div>
              <div className="text-sm text-green-700">Successful</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-red-600">{failureCount}</div>
              <div className="text-sm text-red-700">Failed</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-blue-600">{totalRecords}</div>
              <div className="text-sm text-blue-700">Records Processed</div>
            </div>
          </div>

          {/* Detailed Results */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">File Details</Label>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {results.map((result, index) => (
                <div key={index} className={`flex items-center gap-3 p-3 rounded-md border ${
                  result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex-shrink-0">
                    {result.success ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{result.fileName}</div>
                    {result.success ? (
                      <div className="text-xs text-green-700">
                        {result.recordCount} records processed successfully
                      </div>
                    ) : (
                      <div className="text-xs text-red-700">{result.error}</div>
                    )}
                  </div>
                  <Badge variant={result.success ? "default" : "destructive"} className="text-xs">
                    {result.success ? "Success" : "Failed"}
                  </Badge>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ManualDataUploadModal = ({ isOpen, onClose, station, manualConfigs, productId, onSubmissionSaved }: ManualDataUploadModalProps) => {
  const [selectedConfigId, setSelectedConfigId] = useState<string>('');
  const [selectedConfig, setSelectedConfig] = useState<ManualConfig | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);
  const [currentProductId, setCurrentProductId] = useState<string | undefined>(undefined);
  const { toast } = useToast();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modalProductIdRef = useRef<string | undefined>(undefined);

  // Filter configs for this station - memoize to prevent infinite loops
  const stationConfigs = useMemo(() => {
    return manualConfigs.filter(config => 
      config.manufacturing_process_id === station?.id
    );
  }, [manualConfigs, station?.id]);

  useEffect(() => {
    if (selectedConfigId) {
      const config = stationConfigs.find(c => c.id === selectedConfigId);
      setSelectedConfig(config || null);
      // Reset form data when config changes
      setFormData({});
      setUploadedFiles([]);
    } else {
      setSelectedConfig(null);
      setFormData({});
      setUploadedFiles([]);
    }
  }, [selectedConfigId, stationConfigs]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedConfigId('');
      setSelectedConfig(null);
      setFormData({});
      setUploadedFiles([]);
      setSubmitting(false);
      setShowSummary(false);
      setUploadResults([]);
      // Reset the ref when modal closes
      modalProductIdRef.current = undefined;
    } else {
      // Store the initial productId in the ref when modal first opens
      if (productId && modalProductIdRef.current === undefined) {
        modalProductIdRef.current = productId;
        setCurrentProductId(productId);
      }
    }
  }, [isOpen, productId]);

  const handleFieldChange = (fieldId: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const generateSubmissionName = (fields: LogField[], formData: Record<string, any>): string => {
    const selectedFields = fields.filter(field => field.useInName);
    
    if (selectedFields.length === 0) {
      // Fallback to timestamp if no fields selected
      return `#${Date.now()}`;
    }

    const nameParts = selectedFields.map(field => {
      const value = formData[field.id];
      if (!value || (typeof value === 'string' && !value.trim())) {
        return null;
      }
      return `${field.title}: ${value}`;
    }).filter(Boolean);

    if (nameParts.length === 0) {
      // Fallback to timestamp if selected fields are empty
      return `#${Date.now()}`;
    }

    const result = nameParts.join(', ');
    return result;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      setUploadedFiles(Array.from(files));
    }
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const currentSlug = getCurrentTenantSlug();
    
    const workspace = currentSlug ? await WorkspaceService.getWorkspaceBySlug(currentSlug) : null;
    
    // Use the productId prop, with fallback to the stored ref value
    const effectiveProductId = productId || modalProductIdRef.current;
    
    if (!selectedConfig || !station || !workspace || !effectiveProductId) {
      toast({
        title: "Error",
        description: "Missing required information",
        variant: "destructive",
      });
      return;
    }

    // Handle data configuration (file upload with parsing)
    if (selectedConfig.data_type === 'data') {
      
      if (uploadedFiles.length === 0) {
        toast({
          title: "Files required",
          description: "Please select at least one file to upload",
          variant: "destructive",
        });
        return;
      }

      setSubmitting(true);
      const results: UploadResult[] = [];

      try {
        // Get parsing configuration from database
        const parsingConfigId = selectedConfig.config_data.parsing_configuration_id;
        
        if (!parsingConfigId) {
          throw new Error('No parsing configuration found for this data collection');
        }

        const { data: parsingConfig, error: parsingError } = await (supabase as any)
          .from('parsing_configurations')
          .select('*')
          .eq('id', parsingConfigId)
          .single();

        if (parsingError) {
          console.error('Parsing error:', parsingError);
          throw new Error(`Failed to fetch parsing configuration: ${parsingError.message}`);
        }

        // Process each file according to the parsing configuration
        for (const file of uploadedFiles) {
          const result = await parseAndValidateFile(file, parsingConfig, selectedConfig.config_data.part_configuration_id);
          results.push(result);
        }

        setUploadResults(results);
        setShowSummary(true);

        // Call callback to refresh submission counts
        if (onSubmissionSaved) {
          onSubmissionSaved();
        }

        const successCount = results.filter(r => r.success).length;
        const totalRecords = results.filter(r => r.success).reduce((sum, r) => sum + (r.recordCount || 0), 0);

        if (successCount === uploadedFiles.length) {
          toast({
            title: "All files processed successfully",
            description: `${successCount} files uploaded with ${totalRecords} total records`,
          });
        } else if (successCount > 0) {
          toast({
            title: "Partial success",
            description: `${successCount}/${uploadedFiles.length} files processed successfully`,
          });
        } else {
          toast({
            title: "Upload failed",
            description: "No files were processed successfully",
            variant: "destructive",
          });
        }

      } catch (error) {
        console.error('Error processing files:', error);
        toast({
          title: "Error",
          description: error instanceof Error ? error.message : "Failed to process files",
          variant: "destructive",
        });
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Handle form configuration (existing logic)
    if (!selectedConfig.config_data.fields) {
      toast({
        title: "Error",
        description: "Invalid form configuration",
        variant: "destructive",
      });
      return;
    }

    // Validate required fields
    const requiredFields = selectedConfig.config_data.fields.filter(field => field.required);
    const missingFields = requiredFields.filter(field => {
      const value = formData[field.id];
      return !value || (typeof value === 'string' && !value.trim());
    });

    if (missingFields.length > 0) {
      toast({
        title: "Required fields missing",
        description: `Please fill in: ${missingFields.map(f => f.title).join(', ')}`,
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      // Generate descriptive submission name
      const submissionName = generateSubmissionName(selectedConfig.config_data.fields, formData);
      
      // Calculate next submission number by counting existing submissions for this config
      const { count: existingCount, error: countError } = await supabase
        .from('watt_form_submissions')
        .select('*', { count: 'exact', head: true })
        .eq('config_id', selectedConfig.id);

      if (countError) {
        console.error('Error counting existing submissions:', countError);
        toast({
          title: "Error",
          description: "Failed to calculate submission number",
          variant: "destructive",
        });
        return;
      }

      const nextSubmissionNumber = (existingCount || 0) + 1;
      
      // Save form submission to database
      const { error } = await supabase
        .from('watt_form_submissions')
        .insert({
          workspace_id: workspace.id,
          product_id: effectiveProductId,
          manufacturing_process_id: station.id,
          config_id: selectedConfig.id,
          form_title: selectedConfig.form_title,
          submission_data: formData as any,
          submission_name: submissionName,
          submission_number: nextSubmissionNumber,
          submitted_by: user.id
        });

      if (error) {
        console.error('Error saving form submission:', error);
        toast({
          title: "Error",
          description: "Failed to submit form data",
          variant: "destructive",
        });
        return;
      }

      // Call callback to refresh submission counts
      if (onSubmissionSaved) {
        onSubmissionSaved();
      }
      
      toast({
        title: "Data submitted successfully",
        description: `${selectedConfig.form_title} data has been recorded for ${station.name}`,
      });

      handleClose();
    } catch (error) {
      console.error('Error submitting form data:', error);
      toast({
        title: "Error",
        description: "Failed to submit form data",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const parseAndValidateFile = async (file: File, parsingConfig: any, partConfigurationId: string): Promise<UploadResult> => {
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length === 0) {
        return {
          fileName: file.name,
          success: false,
          error: 'File is empty'
        };
      }

      const config = parsingConfig.config_data;
      const metadata: any = {
        file_name: file.name,
        upload_timestamp: new Date().toISOString()
      };
      const measurements: any[] = [];

      // Parse metadata from the top of the file
      if (config.metadataMappings) {
        for (let i = 0; i < Math.min(10, lines.length); i++) {
          const line = lines[i].trim();
          
          config.metadataMappings.forEach((mapping: any) => {
            if (mapping.sampleDataKey && line.includes(mapping.sampleDataKey)) {
              
              // Use the delimiter from the parsing configuration
              const delimiter = config.delimiter || ',';
              const parts = delimiter === 'whitespace' 
                ? line.split(/\s+/)
                : line.split(delimiter).map(part => part.trim());
              
              // Find the value after the key
              const keyIndex = parts.findIndex(part => part.includes(mapping.sampleDataKey));
              if (keyIndex !== -1 && keyIndex + 1 < parts.length) {
                const value = parts[keyIndex + 1];
                metadata[mapping.targetField] = value;
              }
            }
          });
        }
      }

      // Add dateFormat and timeFormat from parsing config to metadata
      if (config.dateFormat) {
        metadata.date_format = config.dateFormat;
      }
      if (config.timeFormat) {
        metadata.time_format = config.timeFormat;
      }

      // Find the header row for measurement data
      let headerRowIndex = -1;
      let headers: string[] = [];

      // Look for measurement data header (based on field mappings)
      const expectedColumns = config.fieldMappings?.map((fm: any) => fm.csvColumn) || [];
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        const hasExpectedColumns = expectedColumns.some((col: string) => 
          line.toLowerCase().includes(col.toLowerCase())
        );
        
        if (hasExpectedColumns) {
          headers = config.delimiter === 'whitespace' 
            ? line.split(/\s+/).filter(h => h.trim())
            : line.split(config.delimiter || ',').map(h => h.trim().replace(/"/g, ''));
          headerRowIndex = i;
          break;
        }
      }

      if (headerRowIndex === -1) {
        return {
          fileName: file.name,
          success: false,
          error: 'Could not find measurement data header in file'
        };
      }

      // Parse measurement data rows
      for (let i = headerRowIndex + 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        const values = config.delimiter === 'whitespace'
          ? line.split(/\s+/)
          : line.split(config.delimiter || ',').map(v => v.trim().replace(/"/g, ''));
        
        if (values.length >= headers.length - 2) { // Allow some flexibility
          const measurement: any = {};
          
          // Apply field mappings from configuration
          config.fieldMappings?.forEach((mapping: any) => {
            const headerIndex = headers.findIndex(h => 
              h.toLowerCase() === mapping.csvColumn.toLowerCase()
            );
            
            if (headerIndex !== -1 && headerIndex < values.length && values[headerIndex]) {
              const value = values[headerIndex].trim();
              
              // Apply data type conversion
              if (mapping.isRequired || value) {
                if (mapping.targetField.includes('value') || mapping.targetField.includes('deviation')) {
                  const numValue = parseFloat(value);
                  if (!isNaN(numValue)) {
                    measurement[mapping.targetField] = numValue;
                  }
                } else {
                  measurement[mapping.targetField] = value;
                }
              }
            }
          });

          // Validate required fields are present
          const hasRequiredFields = config.fieldMappings?.every((mapping: any) => {
            if (mapping.isRequired) {
              return measurement[mapping.targetField] !== undefined;
            }
            return true;
          });

          if (hasRequiredFields) {
            measurements.push(measurement);
          }
        }
      }

      if (measurements.length === 0) {
        return {
          fileName: file.name,
          success: false,
          error: 'No valid measurement data found in file'
        };
      }

      // Save to sample_data table
      const { error: insertError } = await (supabase as any)
        .from('sample_data')
        .insert({
          part_configuration_id: partConfigurationId,
          parsing_configuration_id: parsingConfig.id,
          metadata: metadata,
          measurements: measurements,
          sample_date: new Date().toISOString()
        });

      if (insertError) {
        console.error('Database insert error:', insertError);
        return {
          fileName: file.name,
          success: false,
          error: `Database error: ${insertError.message}`
        };
      }

      return {
        fileName: file.name,
        success: true,
        recordCount: measurements.length
      };

    } catch (error) {
      return {
        fileName: file.name,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  };

  const handleClose = () => {
    setSelectedConfigId('');
    setSelectedConfig(null);
    setFormData({});
    setUploadedFiles([]);
    setSubmitting(false);
    setShowSummary(false);
    setUploadResults([]);
    
    onClose();
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Reset file input value
    }
  };

  const renderField = (field: LogField) => {
    const value = formData[field.id] || '';

    const fieldIcon = {
      text: Type,
      number: Hash,
      date: Calendar,
      dropdown: ChevronDown,
      textarea: AlignLeft,
      boolean: ToggleLeft,
      radio: Circle
    };

    const Icon = fieldIcon[field.type];

    switch (field.type) {
      case 'text':
        return (
          <Input
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={`Enter ${field.title.toLowerCase()}`}
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={`Enter ${field.title.toLowerCase()}`}
          />
        );

      case 'date':
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
          />
        );

      case 'dropdown':
        return (
          <Select
            value={value}
            onValueChange={(val) => handleFieldChange(field.id, val)}
          >
            <SelectTrigger>
              <SelectValue placeholder={`Select ${field.title.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option, index) => (
                <SelectItem key={index} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'textarea':
        return (
          <Textarea
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={`Enter ${field.title.toLowerCase()}`}
            rows={3}
          />
        );

      case 'boolean':
        return (
          <div className="flex items-center space-x-2">
            <Switch
              checked={value === true}
              onCheckedChange={(checked) => handleFieldChange(field.id, checked)}
            />
            <Label>{value === true ? 'Yes' : 'No'}</Label>
          </div>
        );

      case 'radio':
        return (
          <RadioGroup
            value={value}
            onValueChange={(val) => handleFieldChange(field.id, val)}
          >
            {field.options?.map((option, index) => (
              <div key={index} className="flex items-center space-x-2">
                <RadioGroupItem value={option} id={`${field.id}-${index}`} />
                <Label htmlFor={`${field.id}-${index}`}>{option}</Label>
              </div>
            ))}
          </RadioGroup>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Manual Data Upload
            </DialogTitle>
            <DialogDescription>
              Upload data for {station?.name}. Select a configured form and fill in the required information.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Form Selection */}
            <div className="space-y-2">
              <Label htmlFor="form-select">Select Form</Label>
              {stationConfigs.length > 0 ? (
                <Select
                  value={selectedConfigId}
                  onValueChange={setSelectedConfigId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a form to fill out" />
                  </SelectTrigger>
                  <SelectContent>
                    {stationConfigs.map((config) => (
                      <SelectItem key={config.id} value={config.id}>
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          {config.form_title}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No forms configured for this station</p>
                  <p className="text-sm">Configure a form first using "Configure Data Collection"</p>
                </div>
              )}
            </div>

            {/* Data Configuration - File Upload */}
            {selectedConfig && selectedConfig.data_type === 'data' && (
              <div className="space-y-4">
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    {selectedConfig.form_title}
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="file-upload">Upload Data Files</Label>
                      <Input
                        id="file-upload"
                        type="file"
                        multiple
                        accept=".csv,.json,.txt,.xlsx"
                        onChange={handleFileChange}
                        className="file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/80"
                        ref={fileInputRef}
                      />
                      <p className="text-xs text-muted-foreground">
                        You can select multiple files to upload at once
                      </p>
                    </div>

                    {/* Selected Files List */}
                    {uploadedFiles.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Selected Files ({uploadedFiles.length})</Label>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {uploadedFiles.map((file, index) => (
                            <div key={index} className="flex items-center justify-between bg-muted/50 p-2 rounded-md">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                <span className="text-sm truncate">{file.name}</span>
                                <span className="text-xs text-muted-foreground flex-shrink-0">
                                  ({(file.size / 1024).toFixed(1)} KB)
                                </span>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeFile(index)}
                                className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Total size: {(uploadedFiles.reduce((sum, file) => sum + file.size, 0) / 1024).toFixed(1)} KB
                        </div>
                      </div>
                    )}
                    
                    {selectedConfig.config_data.dataConfig && (
                      <div className="bg-muted/50 p-3 rounded-md">
                        <h4 className="text-sm font-medium mb-2">Configuration Details:</h4>
                        <div className="text-xs text-muted-foreground space-y-1">
                          <p><strong>Part:</strong> {selectedConfig.config_data.dataConfig.partNumber}</p>
                          <p><strong>Description:</strong> {selectedConfig.config_data.dataConfig.description}</p>
                          <p><strong>Visual Requirement:</strong> {selectedConfig.config_data.dataConfig.visualRequirement}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Form Configuration - Form Fields */}
            {selectedConfig && selectedConfig.data_type !== 'data' && selectedConfig.config_data.fields && (
              <div className="space-y-4">
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    {selectedConfig.form_title}
                  </h3>
                  
                  {selectedConfig.config_data.fields.map((field) => (
                    <div key={field.id} className="space-y-2">
                      <Label className="flex items-center gap-2">
                        {(() => {
                          const fieldIcon = {
                            text: Type,
                            number: Hash,
                            date: Calendar,
                            dropdown: ChevronDown,
                            textarea: AlignLeft,
                            boolean: ToggleLeft,
                            radio: Circle
                          };
                          const Icon = fieldIcon[field.type];
                          return <Icon className="w-4 h-4" />;
                        })()}
                        {field.title}
                        {field.required && <span className="text-red-500">*</span>}
                      </Label>
                      {renderField(field)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={handleClose}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={!selectedConfig || submitting || (selectedConfig.data_type === 'data' && uploadedFiles.length === 0)}
                className="gap-2"
              >
                <Upload className="w-4 h-4" />
                {submitting ? "Submitting..." : selectedConfig?.data_type === 'data' ? `Upload ${uploadedFiles.length > 0 ? uploadedFiles.length : ''} File${uploadedFiles.length !== 1 ? 's' : ''}` : "Submit Data"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Summary Modal */}
      <UploadSummaryModal
        isOpen={showSummary}
        onClose={() => {
          setShowSummary(false);
          handleClose(); // Close main modal after summary
        }}
        results={uploadResults}
      />
    </>
  );
};

export { ManualDataUploadModal }; 